import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  commitTradesImport,
  createTrade,
  deleteTrade,
  getTrades,
  previewTradesCsv,
  updateTrade,
} from "./StockTradeService";
import { getTradingAccounts } from "./TradingAccountService";
import { downloadCsv, toCsv } from "./StockCsvUtils";

export default function StockTradeList({ refreshKey, filterAccountId, filterSymbol, onClearFilter }) {
  const [trades, setTrades] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [editId, setEditId] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const importRef = useRef(null);
  const importKeyRef = useRef(null);
  const [importAccountId, setImportAccountId] = useState("");
  const [importPreview, setImportPreview] = useState(null);
  const [importActions, setImportActions] = useState({});
  const [form, setForm] = useState({
    tradingAccountId: "",
    symbol: "",
    stockName: "",
    tradeType: "BUY",
    quantity: "",
    price: "",
    fees: "",
    taxes: "",
    tradeDate: "",
    notes: "",
  });

  useEffect(() => {
    load();
  }, [refreshKey]);

  useEffect(() => {
    if (filterAccountId) {
      setImportAccountId(filterAccountId);
    }
  }, [filterAccountId]);

  const load = async () => {
    const [data, acc] = await Promise.all([getTrades(), getTradingAccounts()]);
    setTrades(data || []);
    setAccounts(acc || []);
  };

  const accountLabelById = useMemo(() => {
    const map = new Map();
    accounts.forEach((a) => {
      const label = `${a.brokerName} (${a.accountNumber || a.id})`;
      map.set(a.id, label);
    });
    return map;
  }, [accounts]);

  const filteredTrades = useMemo(() => {
    return trades.filter((t) => {
      if (filterAccountId && t.tradingAccountId !== filterAccountId) return false;
      if (filterSymbol && t.symbol !== filterSymbol) return false;
      return true;
    });
  }, [trades, filterAccountId, filterSymbol]);

  const totals = useMemo(() => {
    let buyValue = 0;
    let sellValue = 0;
    let fees = 0;
    let taxes = 0;
    let buyQty = 0;
    let sellQty = 0;
    let realizedPnl = 0;
    const grouped = new Map();
    const groupPnlByKey = new Map();
    filteredTrades.forEach((t) => {
      const key = `${t.tradingAccountId || ""}::${t.symbol || ""}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(t);
      const qty = Number(t.quantity || 0);
      const price = Number(t.price || 0);
      const amount = qty * price;
      if (t.tradeType === "SELL") {
        sellQty += qty;
        sellValue += amount;
      } else {
        buyQty += qty;
        buyValue += amount;
      }
      fees += Number(t.fees || 0);
      taxes += Number(t.taxes || 0);
    });
    grouped.forEach((list, key) => {
      let gBuyQty = 0;
      let gSellQty = 0;
      let gBuyValue = 0;
      let gSellValue = 0;
      let gFees = 0;
      let gTaxes = 0;
      list.forEach((t) => {
        const qty = Number(t.quantity || 0);
        const price = Number(t.price || 0);
        if (t.tradeType === "SELL") {
          gSellQty += qty;
          gSellValue += qty * price;
        } else {
          gBuyQty += qty;
          gBuyValue += qty * price;
        }
        gFees += Number(t.fees || 0);
        gTaxes += Number(t.taxes || 0);
      });
      const avgBuy = gBuyQty === 0 ? 0 : gBuyValue / gBuyQty;
      const groupPnl = gSellValue - avgBuy * gSellQty - gFees - gTaxes;
      realizedPnl += groupPnl;
      groupPnlByKey.set(key, groupPnl);
    });
    const netQty = buyQty - sellQty;
    const netCash = sellValue - buyValue - fees - taxes;
    return { buyValue, sellValue, fees, taxes, netQty, netCash, realizedPnl, groupPnlByKey };
  }, [filteredTrades]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete trade?")) return;
    await deleteTrade(id);
    load();
  };

  const startEdit = (t) => {
    setEditId(t.id);
    setForm({
      tradingAccountId: t.tradingAccountId || "",
      symbol: t.symbol || "",
      stockName: t.stockName || "",
      tradeType: t.tradeType || "BUY",
      quantity: t.quantity ?? "",
      price: t.price ?? "",
      fees: t.fees ?? "",
      taxes: t.taxes ?? "",
      tradeDate: t.tradeDate || "",
      notes: t.notes || "",
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setForm({
      tradingAccountId: "",
      symbol: "",
      stockName: "",
      tradeType: "BUY",
      quantity: "",
      price: "",
      fees: "",
      taxes: "",
      tradeDate: "",
      notes: "",
    });
  };

  const saveEdit = async () => {
    if (!editId || !form.tradingAccountId) return;
    await updateTrade(editId, {
      tradingAccountId: form.tradingAccountId,
      symbol: form.symbol,
      stockName: form.stockName || null,
      tradeType: form.tradeType,
      quantity: form.quantity === "" ? null : Number(form.quantity),
      price: form.price === "" ? null : Number(form.price),
      fees: form.fees === "" ? null : Number(form.fees),
      taxes: form.taxes === "" ? null : Number(form.taxes),
      tradeDate: form.tradeDate || null,
      notes: form.notes || "",
    });
    cancelEdit();
    load();
  };

  const handleExport = () => {
    const headers = [
      "tradingAccountId",
      "symbol",
      "stockName",
      "tradeType",
      "quantity",
      "price",
      "fees",
      "taxes",
      "tradeDate",
      "notes",
      "stockId",
    ];
    const rows = filteredTrades.map((t) => ({
      tradingAccountId: t.tradingAccountId,
      symbol: t.symbol,
      stockName: t.stockName,
      tradeType: t.tradeType,
      quantity: t.quantity,
      price: t.price,
      fees: t.fees,
      taxes: t.taxes,
      tradeDate: t.tradeDate,
      notes: t.notes,
      stockId: t.stockId,
    }));
    downloadCsv("stock-trades.csv", toCsv(headers, rows));
  };

  const handleImport = async (file) => {
    if (!file) return;
    if (!importAccountId) {
      window.alert("Select a trading account before importing.");
      return;
    }
    importKeyRef.current = null;
    setIsImporting(true);
    try {
      const preview = await previewTradesCsv(importAccountId, file);
      const actions = {};
      (preview?.preview || []).forEach((row) => {
        if (row.status === "OK") {
          actions[row.rowIndex] = "CREATE";
        } else if (row.status === "DUPLICATE") {
          actions[row.rowIndex] = "SKIP";
        } else if (row.status === "CONFLICT") {
          actions[row.rowIndex] = "SKIP";
        } else {
          actions[row.rowIndex] = "SKIP";
        }
      });
      setImportPreview(preview);
      setImportActions(actions);
    } catch (err) {
      window.alert(err?.message || "Import failed. Please try again.");
    } finally {
      setIsImporting(false);
      if (importRef.current) importRef.current.value = "";
    }
  };

  const updateAction = (rowIndex, action) => {
    setImportActions((prev) => ({ ...prev, [rowIndex]: action }));
  };

  const applyImport = async () => {
    if (!importPreview?.batchId) return;
    setIsImporting(true);
    try {
      const actions = Object.entries(importActions).map(([rowIndex, action]) => ({
        rowIndex: Number(rowIndex),
        action,
      }));
      const result = await commitTradesImport(importAccountId, {
        batchId: importPreview.batchId,
        actions,
      });
      const imported = result?.importedCount ?? 0;
      const updated = result?.updatedCount ?? 0;
      const skipped = result?.skippedCount ?? 0;
      const errors = Array.isArray(result?.errors) ? result.errors.slice(0, 5).join("\n") : "";
      if (onClearFilter) onClearFilter();
      await load();
      window.alert(
        `Import complete. Imported: ${imported}, Updated: ${updated}, Skipped: ${skipped}.` +
          (errors ? `\nErrors:\n${errors}` : "")
      );
      setImportPreview(null);
      setImportActions({});
      importKeyRef.current = null;
    } catch (err) {
      window.alert(err?.message || "Import commit failed.");
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="card">
      {(filterAccountId || filterSymbol) && (
        <div className="row-actions" style={{ marginBottom: 12 }}>
          <div>
            Showing trades for
            {filterSymbol ? ` ${filterSymbol}` : ""}{" "}
            {filterAccountId ? `(${accountLabelById.get(filterAccountId) || filterAccountId})` : ""}
          </div>
          <button className="btn btn-secondary" onClick={onClearFilter}>
            Clear filter
          </button>
        </div>
      )}
      <div className="stocks-toolbar">
        <div className="stocks-summary">
          <span className="stocks-badge">Net Qty: {totals.netQty}</span>
          <span className="stocks-badge">Buy: {totals.buyValue}</span>
          <span className="stocks-badge">Sell: {totals.sellValue}</span>
          <span className="stocks-badge">Fees: {totals.fees}</span>
          <span className="stocks-badge">Taxes: {totals.taxes}</span>
          <span className={`stocks-badge ${totals.realizedPnl >= 0 ? "stocks-good" : "stocks-bad"}`}>
            Total Earnings: {totals.realizedPnl}
          </span>
          <span className={`stocks-badge ${totals.netCash >= 0 ? "stocks-good" : "stocks-bad"}`}>
            Net Cash: {totals.netCash}
          </span>
        </div>
        <div className="row-actions">
          <button className="btn btn-secondary" onClick={handleExport}>
            Export CSV
          </button>
          <select
            className="input"
            value={importAccountId}
            onChange={(e) => setImportAccountId(e.target.value)}
          >
            <option value="">Import account</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.brokerName} ({a.accountNumber || a.id})
              </option>
            ))}
          </select>
          <input
            ref={importRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e) => handleImport(e.target.files[0])}
          />
          <button
            className="btn btn-primary"
            onClick={() => {
              if (!importAccountId) {
                window.alert("Select a trading account before importing.");
                return;
              }
              if (importRef.current) importRef.current.click();
            }}
          >
            {isImporting ? "Importing..." : "Import CSV"}
          </button>
        </div>
      </div>
      {importPreview && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="card-title">Import Review</div>
          <div className="stocks-summary">
            <span className="stocks-badge">Total: {importPreview.totalRows || 0}</span>
            <span className="stocks-badge">Valid: {importPreview.validRows || 0}</span>
            <span className="stocks-badge">Duplicates: {importPreview.duplicateRows || 0}</span>
            <span className="stocks-badge">Conflicts: {importPreview.conflictRows || 0}</span>
            <span className="stocks-badge">Errors: {importPreview.errorRows || 0}</span>
          </div>
          {Array.isArray(importPreview.errors) && importPreview.errors.length > 0 && (
            <div className="today-toast today-toast-error" style={{ marginTop: 10 }}>
              {importPreview.errors.slice(0, 3).join(" | ")}
            </div>
          )}
          <div className="data-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Row</th>
                  <th>Symbol</th>
                  <th>Type</th>
                  <th>Qty</th>
                  <th>Price</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Action</th>
                  <th>Reason</th>
                </tr>
              </thead>
              <tbody>
                {(importPreview.preview || []).map((row) => (
                  <tr key={row.rowIndex}>
                    <td>{row.rowIndex}</td>
                    <td>{row.symbol}</td>
                    <td>{row.tradeType}</td>
                    <td>{row.quantity}</td>
                    <td>{row.price}</td>
                    <td>{row.tradeDate}</td>
                    <td>{row.status}</td>
                    <td>
                      <select
                        value={importActions[row.rowIndex] || "SKIP"}
                        onChange={(e) => updateAction(row.rowIndex, e.target.value)}
                        disabled={row.status === "ERROR"}
                      >
                        <option value="CREATE">CREATE</option>
                        <option value="UPSERT">UPSERT</option>
                        <option value="SKIP">SKIP</option>
                      </select>
                    </td>
                    <td>{row.error || ""}</td>
                  </tr>
                ))}
                {(importPreview.preview || []).length === 0 && (
                  <tr>
                    <td colSpan="9">No preview rows.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="row-actions" style={{ marginTop: 12 }}>
            <button className="btn btn-primary" onClick={applyImport} disabled={isImporting}>
              {isImporting ? "Applying..." : "Apply Import"}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setImportPreview(null);
                setImportActions({});
              }}
              disabled={isImporting}
            >
              Clear Preview
            </button>
          </div>
        </div>
      )}
      <div className="data-table-wrap">
        <table className="stocks-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Symbol</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Account</th>
              <th>Stock PnL</th>
              <th>Fees</th>
              <th>Taxes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTrades.map((t) => (
              <Fragment key={t.id}>
                <tr>
                  <td>
                    <span className={`stocks-pill ${t.tradeType === "SELL" ? "stocks-pill-sell" : "stocks-pill-buy"}`}>
                      {t.tradeType}
                    </span>
                  </td>
                  <td>{t.symbol}</td>
                  <td>{t.quantity}</td>
                  <td>{t.price}</td>
                  <td>{Number(t.quantity || 0) * Number(t.price || 0)}</td>
                  <td>{t.tradeDate}</td>
                  <td>{accountLabelById.get(t.tradingAccountId) || t.tradingAccountId}</td>
                  <td
                    className={
                      (totals.groupPnlByKey?.get(`${t.tradingAccountId || ""}::${t.symbol || ""}`) ?? 0) >= 0
                        ? "stocks-good"
                        : "stocks-bad"
                    }
                  >
                    {totals.groupPnlByKey?.get(`${t.tradingAccountId || ""}::${t.symbol || ""}`) ?? 0}
                  </td>
                  <td>{t.fees}</td>
                  <td>{t.taxes}</td>
                  <td>
                    <button className="btn btn-secondary" onClick={() => startEdit(t)}>
                      Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDelete(t.id)}>
                      Delete
                    </button>
                  </td>
                </tr>
                {editId === t.id && (
                  <tr>
                    <td colSpan="11">
                      <div className="stack-form stocks-inline-form">
                        <select
                          value={form.tradingAccountId}
                          onChange={(e) => setForm({ ...form, tradingAccountId: e.target.value })}
                          required
                        >
                          <option value="">Select trading account</option>
                          {accounts.map((a) => (
                            <option key={a.id} value={a.id}>
                              {a.brokerName} ({a.accountNumber || a.id})
                            </option>
                          ))}
                        </select>

                        <input
                          value={form.symbol}
                          onChange={(e) => setForm({ ...form, symbol: e.target.value })}
                          placeholder="Symbol"
                          required
                        />
                        <input
                          value={form.stockName}
                          onChange={(e) => setForm({ ...form, stockName: e.target.value })}
                          placeholder="Stock name"
                        />
                        <select
                          value={form.tradeType}
                          onChange={(e) => setForm({ ...form, tradeType: e.target.value })}
                        >
                          <option value="BUY">BUY</option>
                          <option value="SELL">SELL</option>
                        </select>
                        <input
                          type="number"
                          value={form.quantity}
                          onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                          placeholder="Quantity"
                          required
                        />
                        <input
                          type="number"
                          value={form.price}
                          onChange={(e) => setForm({ ...form, price: e.target.value })}
                          placeholder="Price"
                          required
                        />
                        <input
                          type="number"
                          value={form.fees}
                          onChange={(e) => setForm({ ...form, fees: e.target.value })}
                          placeholder="Fees"
                        />
                        <input
                          type="number"
                          value={form.taxes}
                          onChange={(e) => setForm({ ...form, taxes: e.target.value })}
                          placeholder="Taxes"
                        />
                        <input
                          type="date"
                          value={form.tradeDate}
                          onChange={(e) => setForm({ ...form, tradeDate: e.target.value })}
                        />
                        <input
                          value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          placeholder="Notes"
                        />

                        <div className="row-actions">
                          <button className="btn btn-primary" onClick={saveEdit}>
                            Save
                          </button>
                          <button className="btn btn-secondary" onClick={cancelEdit}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {filteredTrades.length === 0 && (
              <tr>
                <td colSpan="11">No trades found.</td>
              </tr>
            )}
          </tbody>
          {filteredTrades.length > 0 && (
            <tfoot>
              <tr className="stocks-totals-row">
                <td colSpan="2">Totals</td>
                <td>{totals.netQty}</td>
                <td></td>
                <td>{totals.buyValue + totals.sellValue}</td>
                <td></td>
                <td></td>
                <td>{totals.realizedPnl}</td>
                <td>{totals.fees}</td>
                <td>{totals.taxes}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
